import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { COLORES_DE_PORTADA, MODALIDADES, NIVELES, TIPOS } from './piezas';
import { useCategorias, useCrearCurso } from './api';

/**
 * NuevoCurso.tsx — crear un curso. Módulo 11, opción 1.
 *
 * ## La paleta de portada es un grupo de opciones de verdad
 *
 * Eran ocho `<button>` sin nombre accesible: el lector de pantalla anunciaba
 * ocho «botón» idénticos y no había forma de saber cuál estaba elegido. El
 * vanilla lo corrigió con `role="radio"` y `aria-checked`, y aquí se conserva
 * —pero además **responde a las flechas**, que es lo que un grupo de radio
 * promete y ninguno de los tres grupos de la capa React cumplía todavía.
 *
 * ## Publicar o guardar como borrador
 *
 * Dos opciones que cambian quién ve el curso, así que se eligen con su
 * explicación al lado y no con un desplegable de dos líneas.
 */

/** `CourseCreate` acepta hasta 200 caracteres de título. */
const MAXIMO_TITULO = 200;

const VIGENCIAS: Array<[string, string]> = [
  ['', 'Sin vencimiento'], ['6', '6 meses'], ['12', '1 año'],
  ['24', '2 años'], ['36', '3 años'],
];

const PUBLICACION: Array<{ valor: string; icono: string; titulo: string; ayuda: string }> = [
  {
    valor: 'publicado', icono: 'bi-broadcast', titulo: 'Publicar ahora',
    ayuda: 'Visible para los colaboradores de inmediato.',
  },
  {
    valor: 'borrador', icono: 'bi-pencil-square', titulo: 'Guardar como borrador',
    ayuda: 'Solo lo ve el equipo de Bienestar hasta publicarlo.',
  },
];

export function NuevoCurso({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('curso');
  const [categoria, setCategoria] = useState('');
  const [icono, setIcono] = useState('bi-journal-text');
  const [color, setColor] = useState(COLORES_DE_PORTADA[0]![0]);
  const [nivel, setNivel] = useState('');
  const [modalidad, setModalidad] = useState('');
  const [objetivo, setObjetivo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [vigencia, setVigencia] = useState('');
  const [obligatorio, setObligatorio] = useState(false);
  const [publicacion, setPublicacion] = useState('publicado');
  const [error, setError] = useState('');

  const categorias = useCategorias(abierto);
  const crear = useCrearCurso();

  useEffect(() => {
    if (!abierto) return;
    setTitulo(''); setTipo('curso'); setCategoria(''); setIcono('bi-journal-text');
    setColor(COLORES_DE_PORTADA[0]![0]); setNivel(''); setModalidad('');
    setObjetivo(''); setDescripcion(''); setVigencia(''); setObligatorio(false);
    setPublicacion('publicado'); setError('');
  }, [abierto]);

  function enviar() {
    const limpio = titulo.trim();
    if (!limpio) {
      setError('El título es obligatorio.');
      return;
    }
    crear.mutate(
      {
        title: limpio,
        course_type: tipo,
        category_id: categoria ? Number(categoria) : null,
        cover_icon: icono || 'bi-journal-text',
        cover_icon_color: color,
        description: descripcion.trim() || null,
        objective: objetivo.trim() || null,
        level: nivel || null,
        modality: modalidad || null,
        status: publicacion,
        is_mandatory: obligatorio,
        validity_months: vigencia ? Number(vigencia) : null,
      },
      {
        onSuccess: () => {
          Utils.toast(
            publicacion === 'publicado' ? 'Curso creado y publicado' : 'Curso guardado como borrador',
            'success',
          );
          onCerrar();
        },
        onError: (err) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo crear el curso', 'error'),
      },
    );
  }

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Nuevo curso"
      descripcion="Después podrás añadirle lecciones, quiz y portada desde su detalle."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={crear.isPending}>
            <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Crear curso
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Título" error={error}>
          <Input
            value={titulo}
            maxLength={MAXIMO_TITULO}
            onChange={(e) => { setTitulo(e.target.value); setError(''); }}
            placeholder="Nombre del curso"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tipo">
            <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {Object.entries(TIPOS).map(([k, v]) => (
                <option key={k} value={k}>{v.etiqueta}</option>
              ))}
            </Select>
          </Field>
          <Field
            label="Categoría"
            opcional
            ayuda={categorias.isError
              ? 'No se pudieron cargar. Puedes crear el curso sin categoría.'
              : 'Agrupa el curso en el filtro del catálogo.'}
          >
            <Select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              <option value="">— Sin categoría —</option>
              {(categorias.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Ícono de portada">
          <SelectorDeIcono id="nc-icono" valor={icono} onCambio={setIcono} respaldo="bi-journal-text" />
        </Field>

        <ColorDePortada valor={color} onCambio={setColor} icono={icono} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nivel" opcional>
            <Select value={nivel} onChange={(e) => setNivel(e.target.value)}>
              <option value="">Sin especificar</option>
              {Object.entries(NIVELES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </Field>
          <Field label="Modalidad" opcional>
            <Select value={modalidad} onChange={(e) => setModalidad(e.target.value)}>
              <option value="">Sin especificar</option>
              {Object.entries(MODALIDADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Objetivo de aprendizaje" opcional>
          <Textarea
            rows={2}
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            placeholder="¿Qué logrará el colaborador al completar este curso?"
          />
        </Field>

        <Field label="Descripción" opcional>
          <Textarea
            rows={3}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Describe de qué trata este curso…"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Vigencia del certificado"
            opcional
            ayuda="Para cursos de cumplimiento que exigen recertificación."
          >
            <Select value={vigencia} onChange={(e) => setVigencia(e.target.value)}>
              {VIGENCIAS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </Select>
          </Field>
          <label className="flex min-h-11 cursor-pointer items-start gap-2.5 self-end pb-2 text-3 text-ink-2">
            <input
              type="checkbox"
              checked={obligatorio}
              onChange={(e) => setObligatorio(e.target.checked)}
              className="mt-1 size-4 accent-brand"
            />
            <span>
              <span className="font-semibold text-ink">Obligatorio</span>
              <span className="block text-2 text-muted">
                Entra en el panel de cumplimiento y aparece marcado en el catálogo.
              </span>
            </span>
          </label>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-2 font-semibold text-ink-2">Publicación</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {PUBLICACION.map((o) => (
              <label
                key={o.valor}
                className={cn(
                  'flex cursor-pointer gap-2.5 rounded-sm border p-3 text-3',
                  publicacion === o.valor
                    ? 'border-brand-ink bg-brand/[.06]'
                    : 'border-line hover:border-brand-ink',
                )}
              >
                <input
                  type="radio"
                  name="nc-publicacion"
                  value={o.valor}
                  checked={publicacion === o.valor}
                  onChange={() => setPublicacion(o.valor)}
                  className="mt-0.5 size-4 accent-brand"
                />
                <span>
                  <span className="flex items-center gap-1.5 font-semibold text-ink">
                    <i className={cn('bi', o.icono)} aria-hidden="true" /> {o.titulo}
                  </span>
                  <span className="mt-0.5 block text-2 text-muted">{o.ayuda}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </Drawer>
  );
}

/**
 * Los ocho colores de portada, como un grupo de opciones navegable.
 *
 * Las flechas mueven la elección, y no es un adorno: un `role="radiogroup"` que
 * no responde a las flechas **promete un comportamiento que no cumple**, y eso
 * es peor que no anunciarlo. Quedaba abierto desde el módulo 7 para los tres
 * grupos de la capa React; este es el primero que lo trae.
 */
function ColorDePortada({
  valor, onCambio, icono,
}: {
  valor: string;
  onCambio: (v: string) => void;
  icono: string;
}) {
  const botones = useRef<Array<HTMLButtonElement | null>>([]);

  // La elección Y el foco. Hasta el 2026-09-21 solo se movía la elección: el
  // anillo se quedaba en el color anterior —ya con `tabIndex` -1— y el lector no
  // anunciaba nada. Salió al escribir el grupo de iconos de la portada.
  function mover(delta: number) {
    const i = COLORES_DE_PORTADA.findIndex(([c]) => c === valor);
    const n = (i + delta + COLORES_DE_PORTADA.length) % COLORES_DE_PORTADA.length;
    onCambio(COLORES_DE_PORTADA[n]![0]);
    botones.current[n]?.focus();
  }

  return (
    <div>
      <span id="nc-color" className="mb-1.5 block text-2 font-semibold text-ink-2">
        Color de fondo
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="radiogroup"
          aria-labelledby="nc-color"
          className="flex flex-wrap gap-2"
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); mover(1); }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); mover(-1); }
          }}
        >
          {COLORES_DE_PORTADA.map(([c, nombre], idx) => {
            const puesto = c === valor;
            return (
              <button
                key={c}
                ref={(el) => { botones.current[idx] = el; }}
                type="button"
                role="radio"
                aria-checked={puesto}
                aria-label={nombre}
                title={nombre}
                // Solo el elegido entra en el orden de tabulación: el grupo es
                // UNA parada, y dentro se mueve con las flechas.
                tabIndex={puesto ? 0 : -1}
                onClick={() => onCambio(c)}
                className={cn(
                  'size-8 rounded-sm focus-visible:focus-anillo',
                  puesto ? 'outline outline-2 outline-offset-2 outline-brand-ink' : '',
                )}
                style={{ background: c }}
              />
            );
          })}
        </div>
        <span
          className="ml-auto flex size-10 items-center justify-center rounded-sm"
          style={{ background: valor }}
          aria-hidden="true"
        >
          <i className={cn('bi text-5 leading-none text-white', icono || 'bi-journal-text')} />
        </span>
      </div>
    </div>
  );
}
